// ─────────────────────────────────────────────────────────────────────────────
// InvalidLink — terminal dead-end state.
//
// Shown when:
//   1. The URL's token doesn't match the 43-char base64url regex
//      (caught in App.tsx before any API call)
//   2. The BFF returned CHECKOUT_NOT_FOUND (caught in Checkout.tsx)
//
// Both cases mean the same thing from the payer's POV: this link is dead.
// There is no recovery action from the browser — the payer's path forward
// is to contact whoever sent them the link, or start over from the merchant.
//
// No polling, no retry button. A malformed or unknown token cannot become
// valid; offering retry would be dishonest UX.
// ─────────────────────────────────────────────────────────────────────────────

import { Topbar } from './Topbar';
import { Footer } from './Footer';

export interface InvalidLinkProps {
  /**
   * Optional override for the default body copy. When the BFF returns
   * CHECKOUT_NOT_FOUND, its structured error message is passed here so
   * the payer sees "This checkout link is not valid or has expired"
   * from the server rather than our default malformed-URL wording.
   */
  message?: string;
}

export function InvalidLink({ message }: InvalidLinkProps) {
  return (
    <main className="shell">
      <section className="checkout-card">
        <Topbar returnUrl={null} />
        <div className="method-divider" />
        <div className="invalid-link">
          <div className="state-icon-wrap">
            {/* link-broken glyph: a chain-link with a break */}
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1.5 1.5" />
              <path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1.5-1.5" />
              <path d="M2 2l20 20" strokeLinecap="round" />
            </svg>
          </div>
          <h1 className="state-heading">This link isn&apos;t valid</h1>
          <p className="state-desc">
            {message ??
              'The checkout link you followed is invalid or has expired. Please contact the merchant who sent it to you, or return to their site to start a new payment.'}
          </p>
        </div>
        <div className="method-divider" />
        <Footer />
      </section>
    </main>
  );
}
